import React, {useState} from 'react';
import {useStore} from '../../../../state/store';
import {state} from '../../../../state/utils';
import {
  StickerCategory,
  StickerCategoryMessages,
} from '../../../../config/default-stickers';
import {ButtonBase} from '@common/ui/buttons/button-base';
import {Button} from '@common/ui/buttons/button';
import {StickerList} from './sticker-list';
import {Trans} from '@common/i18n/trans';
import {EmojiEmotionsIcon} from '@common/icons/material/EmojiEmotions';
import {PlaceIcon} from '@common/icons/material/Place';
import {DirectionsCarFilledIcon} from '@common/icons/material/DirectionsCarFilled';
import {BeachAccessIcon} from '@common/icons/material/BeachAccess';
import {MessageIcon} from '@common/icons/material/Message';
import {BrushIcon} from '@common/icons/material/Brush';

type CategoryStickerMenuProps = {
  category: StickerCategory;
};

function CategoryStickerMenu({category}: CategoryStickerMenuProps) {
  return (
    <div className="h-full flex flex-col overflow-auto">
      {/* Header sadece kategori adı ile */}
      <div className="flex justify-center items-center p-4 border-b border-divider">
        <div className="text-sm font-medium capitalize">
          {StickerCategoryMessages[category.name] ?
            <Trans {...StickerCategoryMessages[category.name]} /> :
            category.name
          }
        </div>
      </div>

      {/* Sticker list */}
      <div className="flex-1 overflow-auto p-4">
        <StickerList category={category} />
      </div>
    </div>
  );
}

type StickerNavProps = {
  onClose?: () => void;
  onApply?: () => void;
};

export function StickerNav({onClose, onApply}: StickerNavProps = {}) {
  const categories = useStore(s => s.config.tools?.stickers?.items) || [];
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [activeCategory, setActiveCategory] = useState<StickerCategory | null>(null);
  const [selectedSticker, setSelectedSticker] = useState<string | null>(null);

  const getCategoryIcon = (categoryName: string) => {
    switch (categoryName) {
      case 'emoticons':
        return <EmojiEmotionsIcon className="icon-md" />;
      case 'landmarks':
        return <PlaceIcon className="icon-md" />;
      case 'transportation':
        return <DirectionsCarFilledIcon className="icon-md" />;
      case 'beach':
        return <BeachAccessIcon className="icon-md" />;
      case 'bubbles':
        return <MessageIcon className="icon-md" />;
      case 'doodles':
        return <BrushIcon className="icon-md" />;
      default:
        return <EmojiEmotionsIcon className="icon-md" />;
    }
  };

  const openCategoryMenu = (category: StickerCategory) => {
    setActiveCategory(category);
    setShowCategoryMenu(true);
  };

  // Navbar Close butonunun davranışını kontrol et
  const handleNavbarClose = () => {
    if (showCategoryMenu) {
      // Kategori menüsünden ana menüye dön
      setShowCategoryMenu(false);
      setActiveCategory(null);
      setSelectedSticker(null);
    } else {
      // Ana kategori listesinden sticker tool'dan çık
      // Eğer callback yoksa state().cancelChanges() kullan
      if (onClose) {
        onClose();
      } else {
        // Fallback: global state kullan
        state().cancelChanges();
      }
    }
  };

  // Navbar Apply butonunun davranışını kontrol et
  const handleNavbarApply = () => {
    if (showCategoryMenu) {
      // Kategori menüsünden kategori listesine dön (sticker seçildiyse)
      setShowCategoryMenu(false);
      setActiveCategory(null);
      setSelectedSticker(null);
      // Apply işlemini yapmadan sadece geri dön
    } else {
      // Ana kategori listesinden tool'dan çık
      if (onApply) {
        onApply();
      } else {
        state().applyChanges();
      }
    }
  };

  // Parent component'e callback'leri expose et
  React.useEffect(() => {
    // Bu callback'leri parent component navbar butonlarına bağlamak için
    // global state veya context kullanılması gerekebilir
    if (typeof window !== 'undefined') {
      (window as any).stickerNavHandlers = {
        close: handleNavbarClose,
        apply: handleNavbarApply,
        isInCategoryMenu: showCategoryMenu
      };
    }
  }, [showCategoryMenu, onClose, onApply]);

  if (showCategoryMenu && activeCategory) {
    return (
      <CategoryStickerMenu
        category={activeCategory}
      />
    );
  }

  return (
    <div className="pt-6">
      <div className="flex gap-10 overflow-x-auto px-4">
        {categories.map((category: StickerCategory) => {
          const msg = StickerCategoryMessages[category.name];
          return (
            <ButtonBase
              key={category.name}
              variant="flat"
              color="paper"
              className="flex-col flex-shrink-0 w-68 h-68"
              radius="none"
              onClick={() => openCategoryMenu(category)}
            >
              <div className="mb-1">
                {getCategoryIcon(category.name)}
              </div>
              <div className="mt-6 text-xs capitalize max-w-[90%] overflow-hidden overflow-ellipsis">
                {msg ? <Trans {...msg} /> : category.name}
              </div>
            </ButtonBase>
          );
        })}
      </div>
    </div>
  );
}
